/**
 * Éditeur Notion-like d'une leçon V2 (flux plat de blocs réordonnables).
 *
 * UX : chaque bloc s'affiche en consultation (LessonBlockCard) ; cliquer
 * « Éditer » bascule en mode édition (LessonBlockShell). Split par sélection,
 * fusion avec le bloc précédent (même type), insertion, duplication, suppression.
 * Les steps `content` peuvent être liés à un corrigé/distracteur d'exercice.
 *
 * Mutations : useLessonBlocksMutate → edge fn lesson-blocks-mutate (recalcule
 * aussi lessons.content_md pour la vue élève).
 */

import { useMemo, useState } from 'react';
import { Button } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import {
  lessonV2QueryKey,
  nextClientId,
  useLessonBlockLinkMutate,
  useLessonBlocksMutate,
  useLessonV2,
} from '@/lib/lesson-v2/api';
import type {
  LessonBlock,
  LessonBlockLink,
  LessonBlockMutationOp,
  LessonBlockSection,
  LessonBlockType,
  LessonV2Payload,
} from '@/lib/lesson-v2/types';
import {
  blocksInSection,
  getPrimaryTextField,
  parseMarkdownToBlocks,
  positionAfter,
  positionAtEnd,
  positionBefore,
} from '@/lib/lesson-v2/block-helpers';
import { useQueryClient } from '@tanstack/react-query';
import { LessonBlockShell } from './BlockShell';
import { LessonBlockCard } from './BlockCard';
import { LinkPicker } from './LinkPicker';

export interface LessonEditorV2Props {
  lessonId: string;
}

const DEFAULT_DATA_FOR: Record<LessonBlockType, Record<string, unknown>> = {
  heading: { text: 'Nouvelle section', level: 2 },
  text: { content_md: 'Nouveau paragraphe…' },
  key_point: { content_md: 'Point clé à retenir…' },
  example: { content_md: 'Exemple travaillé…' },
  note: { body_md: 'Note tuteur…' },
};

export function LessonEditorV2({ lessonId }: LessonEditorV2Props) {
  const query = useLessonV2(lessonId);
  const mutate = useLessonBlocksMutate(lessonId);
  const qc = useQueryClient();
  const [migrating, setMigrating] = useState(false);
  const [migrateError, setMigrateError] = useState<string | null>(null);

  if (query.isPending) {
    return <div className="p-6 text-center text-sm text-slate-500">Chargement de la leçon…</div>;
  }
  if (query.isError) {
    return (
      <div className="p-6 text-center text-sm text-red-600">
        Erreur de chargement. <button onClick={() => query.refetch()}>Réessayer</button>
      </div>
    );
  }
  const payload = query.data!;

  async function migrateToV2() {
    setMigrating(true);
    setMigrateError(null);
    try {
      const { error: upErr } = await supabase
        .from('lessons')
        .update({ content_version: 'v2' })
        .eq('id', lessonId);
      if (upErr) throw new Error(upErr.message);

      const parsed = parseMarkdownToBlocks(payload.meta.content_md ?? '');
      if (parsed.length > 0) {
        const ops: LessonBlockMutationOp[] = parsed.map((b, i) => ({
          op: 'insert',
          client_id: nextClientId(`init-${i}`),
          block_type: b.block_type,
          section: b.section,
          parent_id: null,
          position: i + 1,
          data: b.data,
        }));
        await mutate.mutateAsync(ops);
      }
      qc.invalidateQueries({ queryKey: lessonV2QueryKey(lessonId) });
    } catch (e) {
      setMigrateError((e as Error).message);
    } finally {
      setMigrating(false);
    }
  }

  if (payload.meta.content_version === 'v1') {
    return (
      <div className="space-y-4 p-6 text-center">
        <h2 className="text-lg font-semibold">Cette leçon est en V1 (markdown).</h2>
        <p className="text-sm text-slate-600">
          L'initialisation découpe le contenu markdown en blocs éditables. Le contenu markdown reste
          la source rendue côté élève (recalculé depuis les blocs).
        </p>
        <Button onClick={migrateToV2} disabled={migrating}>
          {migrating ? 'Initialisation…' : '🚀 Initialiser les blocs V2'}
        </Button>
        {migrateError ? <p className="text-sm text-red-600">Échec : {migrateError}</p> : null}
      </div>
    );
  }

  return (
    <V2EditorBody
      lessonId={lessonId}
      chapterId={payload.meta.chapter_id}
      blocks={payload.blocks}
      links={payload.links}
      isMutating={mutate.isPending}
      mutationError={mutate.isError ? (mutate.error as Error) : null}
      onApply={(ops) => mutate.mutate(ops)}
      onResetError={() => mutate.reset()}
    />
  );
}

function V2EditorBody({
  lessonId,
  chapterId,
  blocks,
  links,
  isMutating,
  mutationError,
  onApply,
  onResetError,
}: {
  lessonId: string;
  chapterId: string;
  blocks: LessonBlock[];
  links: LessonBlockLink[];
  isMutating: boolean;
  mutationError: Error | null;
  onApply: (ops: LessonBlockMutationOp[]) => void;
  onResetError: () => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showMeta, setShowMeta] = useState(false);
  const [linkingBlockId, setLinkingBlockId] = useState<string | null>(null);
  const linkMutate = useLessonBlockLinkMutate(lessonId);

  const contentBlocks = useMemo(() => blocksInSection(blocks, 'content'), [blocks]);
  const metaBlocks = useMemo(() => blocksInSection(blocks, 'meta'), [blocks]);

  const linksByBlock = useMemo(() => {
    const map = new Map<string, LessonBlockLink[]>();
    for (const l of links) {
      const arr = map.get(l.lesson_block_id) ?? [];
      arr.push(l);
      map.set(l.lesson_block_id, arr);
    }
    return map;
  }, [links]);

  // ── Op builders ────────────────────────────────────────────────────────────

  function moveBlock(block: LessonBlock, direction: 'up' | 'down') {
    const section = blocksInSection(blocks, block.section);
    const idx = section.findIndex((b) => b.id === block.id);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= section.length) return;
    const newPos =
      direction === 'up' ? positionBefore(section, targetIdx) : positionAfter(section, targetIdx);
    onApply([{ op: 'reorder', id: block.id, position: newPos }]);
  }

  function insertBlock(
    after: LessonBlock | null,
    section: LessonBlockSection,
    type: LessonBlockType,
  ) {
    const sectionList = blocksInSection(blocks, section);
    const insertIdx = after ? sectionList.findIndex((b) => b.id === after.id) : -1;
    const newPos = after ? positionAfter(sectionList, insertIdx) : positionAtEnd(sectionList);
    onApply([
      {
        op: 'insert',
        client_id: nextClientId(type),
        block_type: type,
        section,
        parent_id: null,
        position: newPos,
        data: DEFAULT_DATA_FOR[type],
      },
    ]);
  }

  function duplicateBlock(block: LessonBlock) {
    const sectionList = blocksInSection(blocks, block.section);
    const idx = sectionList.findIndex((b) => b.id === block.id);
    const newPos = positionAfter(sectionList, idx);
    onApply([
      {
        op: 'insert',
        client_id: nextClientId('dup'),
        block_type: block.block_type,
        section: block.section,
        parent_id: null,
        position: newPos,
        data: structuredClone(block.data),
      },
    ]);
  }

  function deleteBlock(block: LessonBlock) {
    if (!confirm(`Supprimer ce bloc ${block.block_type} ?`)) return;
    if (editingId === block.id) setEditingId(null);
    onApply([{ op: 'delete', id: block.id }]);
  }

  function patchData(block: LessonBlock, data: Record<string, unknown>) {
    onApply([{ op: 'update', id: block.id, patch: { data } }]);
  }

  function splitBlockSelection(block: LessonBlock, selStart: number, selEnd: number) {
    const textField = getPrimaryTextField(block.block_type);
    const data = block.data as Record<string, unknown>;
    const text = data[textField];
    if (typeof text !== 'string') return;

    const start = Math.max(0, Math.min(selStart, selEnd, text.length));
    const end = Math.max(0, Math.min(Math.max(selStart, selEnd), text.length));
    if (end <= start) return;

    const before = text.slice(0, start);
    const middle = text.slice(start, end);
    const after = text.slice(end);
    if (middle.trim() === '') {
      alert('La sélection est vide. Sélectionnez du texte non blanc pour découper.');
      return;
    }
    const hasBefore = before.trim() !== '';
    const hasAfter = after.trim() !== '';
    if (!hasBefore && !hasAfter) {
      alert("La sélection couvre l'intégralité du bloc — rien à découper.");
      return;
    }

    const sectionList = blocksInSection(blocks, block.section);
    const idx = sectionList.findIndex((b) => b.id === block.id);
    const next = sectionList[idx + 1];
    const nextPos = next ? next.position : block.position + 2;
    const makeData = (t: string) => ({ ...data, [textField]: t });
    const ops: LessonBlockMutationOp[] = [];
    const insertChild = (t: string, position: number, tag: string): LessonBlockMutationOp => ({
      op: 'insert',
      client_id: nextClientId(tag),
      block_type: block.block_type,
      section: block.section,
      parent_id: null,
      position,
      data: makeData(t),
    });

    if (hasBefore && hasAfter) {
      const middlePos = (block.position + nextPos) / 2;
      const afterPos = (middlePos + nextPos) / 2;
      ops.push({ op: 'update', id: block.id, patch: { data: makeData(before) } });
      ops.push(insertChild(middle, middlePos, 'split-mid'));
      ops.push(insertChild(after, afterPos, 'split-after'));
    } else if (hasBefore && !hasAfter) {
      const middlePos = (block.position + nextPos) / 2;
      ops.push({ op: 'update', id: block.id, patch: { data: makeData(before) } });
      ops.push(insertChild(middle, middlePos, 'split-mid'));
    } else {
      const afterPos = (block.position + nextPos) / 2;
      ops.push({ op: 'update', id: block.id, patch: { data: makeData(middle) } });
      ops.push(insertChild(after, afterPos, 'split-after'));
    }
    onApply(ops);
  }

  function mergeWithPrev(block: LessonBlock) {
    const sectionList = blocksInSection(blocks, block.section);
    const idx = sectionList.findIndex((b) => b.id === block.id);
    if (idx <= 0) return;
    const prev = sectionList[idx - 1]!;
    if (prev.block_type !== block.block_type) {
      alert('Fusion impossible : le bloc précédent doit être du même type.');
      return;
    }
    const textField = getPrimaryTextField(block.block_type);
    onApply([{ op: 'merge', into_id: prev.id, from_id: block.id, text_field: textField }]);
  }

  function removeLink(linkId: string) {
    linkMutate.mutate({ op: 'unlink', link_id: linkId });
  }

  // ── Rendu d'un bloc ──────────────────────────────────────────────────────

  function renderBlockItem(block: LessonBlock, ctx: { isFirst: boolean; isLast: boolean }) {
    const isEditing = editingId === block.id;
    const sectionList = blocksInSection(blocks, block.section);
    const sectionIdx = sectionList.findIndex((b) => b.id === block.id);
    const canMergeWithPrev =
      sectionIdx > 0 && sectionList[sectionIdx - 1]!.block_type === block.block_type;

    if (isEditing) {
      return (
        <LessonBlockShell
          block={block}
          isFirst={ctx.isFirst}
          isLast={ctx.isLast}
          isDisabled={isMutating}
          onMoveUp={() => moveBlock(block, 'up')}
          onMoveDown={() => moveBlock(block, 'down')}
          onInsertAfter={(type) => insertBlock(block, block.section, type)}
          onDuplicate={() => duplicateBlock(block)}
          onDelete={() => deleteBlock(block)}
          onPatchData={(data) => patchData(block, data)}
          onSplitRange={(start, end) => splitBlockSelection(block, start, end)}
          onMergeWithPrev={() => mergeWithPrev(block)}
          canMergeWithPrev={canMergeWithPrev}
          onDoneEditing={() => setEditingId(null)}
        />
      );
    }
    return (
      <LessonBlockCard
        block={block}
        links={linksByBlock.get(block.id) ?? []}
        isFirst={ctx.isFirst}
        isLast={ctx.isLast}
        isDisabled={isMutating}
        onMoveUp={() => moveBlock(block, 'up')}
        onMoveDown={() => moveBlock(block, 'down')}
        onEdit={() => setEditingId(block.id)}
        onDuplicate={() => duplicateBlock(block)}
        onDelete={() => deleteBlock(block)}
        onAddLink={() => setLinkingBlockId(block.id)}
        onRemoveLink={removeLink}
      />
    );
  }

  return (
    <div className="pb-safe mx-auto max-w-4xl space-y-4 px-3 py-6 sm:px-6">
      <div className="flex flex-wrap gap-1">
        <Button
          size="sm"
          variant="outline"
          onClick={() => insertBlock(null, 'content', 'text')}
          disabled={isMutating}
        >
          + Texte
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => insertBlock(null, 'content', 'heading')}
          disabled={isMutating}
        >
          + Titre
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => insertBlock(null, 'content', 'key_point')}
          disabled={isMutating}
        >
          + Point clé
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => insertBlock(null, 'content', 'example')}
          disabled={isMutating}
        >
          + Exemple
        </Button>
      </div>

      {contentBlocks.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          Leçon vide. Commencez par ajouter un titre ou un paragraphe.
        </div>
      ) : (
        <ul className="space-y-2">
          {contentBlocks.map((b, idx) => (
            <li key={b.id}>
              {renderBlockItem(b, { isFirst: idx === 0, isLast: idx === contentBlocks.length - 1 })}
            </li>
          ))}
        </ul>
      )}

      <div className="pt-2">
        <button
          onClick={() => setShowMeta((v) => !v)}
          className="min-h-9 text-xs text-slate-500 underline-offset-2 hover:underline"
        >
          {showMeta ? '▼' : '▶'} Notes tuteur ({metaBlocks.length})
        </button>
        {showMeta ? (
          <div className="mt-3 space-y-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => insertBlock(null, 'meta', 'note')}
              disabled={isMutating}
            >
              + Note
            </Button>
            {metaBlocks.length === 0 ? (
              <p className="rounded-md border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
                Aucune note tuteur.
              </p>
            ) : (
              <ul className="space-y-2">
                {metaBlocks.map((b, idx) => (
                  <li key={b.id}>
                    {renderBlockItem(b, {
                      isFirst: idx === 0,
                      isLast: idx === metaBlocks.length - 1,
                    })}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>

      {isMutating ? (
        <div className="fixed bottom-4 right-4 rounded-full bg-slate-900 px-3 py-1 text-xs text-white shadow-lg">
          Enregistrement…
        </div>
      ) : null}

      {mutationError ? (
        <div className="fixed bottom-4 left-4 right-4 max-w-2xl rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 shadow-lg sm:left-auto">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1">
              <p className="font-medium">Échec de l'enregistrement</p>
              <p className="whitespace-pre-wrap font-mono text-xs">{mutationError.message}</p>
            </div>
            <button
              type="button"
              onClick={onResetError}
              className="min-h-9 rounded p-1 text-red-700 hover:bg-red-100"
              aria-label="Fermer l'erreur"
            >
              ✕
            </button>
          </div>
        </div>
      ) : null}

      <p className="text-xs text-slate-400">
        ⓘ Toutes les modifications sont enregistrées automatiquement.
      </p>

      {linkingBlockId ? (
        <LinkPicker
          lessonId={lessonId}
          chapterId={chapterId}
          lessonBlockId={linkingBlockId}
          onClose={() => setLinkingBlockId(null)}
        />
      ) : null}
    </div>
  );
}

export type { LessonV2Payload };
